import React, { useRef, useEffect } from "react";
import ReactSelect, {
  OptionTypeBase,
  Props as SelectProps,
} from "react-select";
import { useField } from "@unform/core";

interface Props extends SelectProps<OptionTypeBase> {
  name: string;
  divClass?: string;
  isMulti?: any;
  label: string;
}

function Select({ name, divClass, label, ...rest }: Props) {
  const selectRef = useRef(null);
  const { fieldName, defaultValue, registerField, error, clearError } =
    useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      setValue: (ref, value) => {
        ref.select.setValue(value || null);
      },
      clearValue: (ref) => {
        ref.select.clearValue();
      },
      getValue: rest.isMulti
        ? (ref) =>
            ref.state.value?.map((option: OptionTypeBase) => option.value) || []
        : (ref) => (ref.state.value ? ref.state.value.value : ""),
    });
  }, [fieldName, registerField, rest.isMulti]);

  return (
    <div className={divClass || "form-group"}>
      <label
        htmlFor={fieldName}
        className={
          "font-weight-light" + (error ? " text-danger" : " text-secondary")
        }
      >
        {label}
      </label>

      <ReactSelect
        cacheOptions
        className={"font-weight-light" + (error ? " is-invalid" : "")}
        id={fieldName}
        ref={selectRef}
        onFocus={clearError}
        defaultValue={defaultValue}
        noOptionsMessage={() => "Nenhum resultado encontrado"}
        theme={(theme) => ({
          ...theme,
          borderRadius: 12,
          colors: {
            ...theme.colors,
            primary: "hsl(var(--primary))",
            primary75: "hsl(var(--primary) / 0.75)",
            primary50: "hsl(var(--primary) / 0.2)",
            primary25: "hsl(var(--primary) / 0.1)",
            neutral20: error ? "hsl(var(--destructive))" : "hsl(var(--input))",
            neutral30: "hsl(var(--muted-foreground) / 0.5)",
          },
        })}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: "2.75rem",
            boxShadow: state.isFocused ? "0 0 0 3px hsl(var(--primary) / 0.15)" : "none",
          }),
          menu: (base) => ({ ...base, borderRadius: 12, overflow: "hidden" }),
        }}
        {...rest}
      />

      {error && <span className="invalid-feedback">{error}</span>}
    </div>
  );
}

export default Select;
